import { cloneDeep } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { TabPanel } from 'react-tabs';
import { colors } from '../../../../../../../../../../colorVars';
import { addExitTactic } from '../../../../../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/updateTradePlanMessages/addExitTactic';
import { useWebsocketStore } from '../../../../../../../../../../stores/websocketStore/websocketProvider';
import { CancelOrderButton } from '../../../../../../../../../atomic/buttons/cancelOrderButton/CancelOrderButton';
import IncrementButton from '../../../../../../../../../atomic/buttons/incrementButton/IncrementButton';
import { MultiSwitch } from '../../../../../../../../../atomic/buttons/multiSwitch/MultiSwitch';
import TextButton from '../../../../../../../../../atomic/buttons/textButton/TextButton';
import CheckBox from '../../../../../../../../../atomic/formElements/checkBox/CheckBox';
import { EditableTableFieldNoOnBlurUpdate } from '../../../../../../../../../atomic/inputs/editableTableField/EditableFieldNoOnBlurUpdate';
import { TableSelect } from '../../../../../../../../../atomic/inputs/tableSelect/TableSelect';
import { Body3, fontSizes } from '../../../../../../../../../atomic/text/Text';
import { candleMagnitudeValidation } from '../../../../../../../../../forms/tradeplans/AddTradePlan/DataValidation';
import { IndividualPanelHeader } from '../../../../individualTacticPanels/IndividualPanelHeader';
import style from './IndividualAddTacticPanel.module.css';

export const AddCandleMagnitudeTacticPanel = observer(
  // eslint-disable-next-line no-unused-vars
  ({ tradePlanId, softOrderId, addTab, ...rest }) => {
    const { tradePlanStore } = useWebsocketStore();
    const tradePlan = tradePlanStore.tradePlans[tradePlanId];
    const [headerText, setHeaderText] = useState('Add Candle Magnitude');
    const [headerTextColor, setHeaderTextColor] = useState(colors.lightGrey);
    const [addValue, setAddValue] = useState();
    const timeoutRef = useRef(null);

    const [details, setDetails] = useState({
      candleDuration: 1,
      timeUnit: 'MINUTES', // MINUTES, HOURS, DAYS, WEEKS, MONTHS, QUARTERS, YEARS
      numberOfPivots: 0,
      qtyPctPerPivot: [],
      exitInPivotCandle: 'high_low', // high_low or mid
      usePivotsAsTrails: false,
      filterInsideCandleMagnitudes: false,
      useAtr: false,
      ATR: {
        candlesBack: 14, //field # of candles
        atrType: 'WILDER', //options: SMA, WILDER, SMA_NO_GAP. drop down. wilder used 90% of time
        candleDuration: 1, //do candle duration + time unit combo
        timeUnit: 'MINUTES',
      },
      filterFirstMagnitudeBasedOnAtr: false, //think of this section like the repeating design. checkbox "a"
      pctOfAtrForFilter: 0.0, //field "a" as % of atr
      filterAllMagnitudeBasedOnAtr: false,
      pctOfAtrForFilterAllMag: 0,
      useAtrInsteadOfMag: false,
      pctAtrInsteadOfMag: 0,
      generateFromAtrWhenNoPivots: false,
      pctAtrGenerateAtrWhenNoPivots: 100,
      initialStop: 'NONE', //NONE, ATR,CANDLE_RETRACE,CANDLE_HIGH_LOW
      pctAtrInitialStop: 0,
      pctRetraceInitialStop: 0,

      firstTargetStop: 'NONE', //NONE,ATR,CANDLE_RETRACE,CANDLE_HIGH_LOW,BREAK_EVEN
      pctAtrFirstTargetStop: 0,
      pctRetraceFirstTargetStop: 0,
    });

    const addTactic = () => {
      const detailsCopy = cloneDeep(details);
      //convert percent to decimal for all percentage inputs
      detailsCopy.pctOfAtrForFilter = details.pctOfAtrForFilter / 100;
      detailsCopy.pctOfAtrForFilterAllMag =
        details.pctOfAtrForFilterAllMag / 100;
      detailsCopy.pctAtrInsteadOfMag = details.pctAtrInsteadOfMag / 100;
      detailsCopy.pctAtrInitialStop = details.pctAtrInitialStop / 100;
      detailsCopy.pctRetraceInitialStop = details.pctRetraceInitialStop / 100;
      detailsCopy.pctAtrFirstTargetStop = details.pctAtrFirstTargetStop / 100;
      detailsCopy.pctRetraceFirstTargetStop = details.pctRetraceFirstTargetStop / 100;
      detailsCopy.pctAtrGenerateAtrWhenNoPivots = details.pctAtrGenerateAtrWhenNoPivots / 100;
      const validationResult = candleMagnitudeValidation(detailsCopy);
      if (validationResult[0] === false) {
        setHeaderText(validationResult[1]);
        setHeaderTextColor(colors.red);
        const timeout = setTimeout(() => {
          setHeaderText('Add Candle Magnitude');
          setHeaderTextColor(colors.lightGrey);
        }, 2000);
        timeoutRef.current = timeout;
      } else {
        addExitTactic(tradePlanId, {
          tactic: 'CANDLE_MAGNITUDE',
          details: detailsCopy,
        });
        setHeaderText('Candle Magnitude Added');
        setHeaderTextColor(colors.green);
        const timeout = setTimeout(() => {
          setHeaderText('Add Candle Magnitude');
          setHeaderTextColor(colors.lightGrey);
        }, 2000);
        timeoutRef.current = timeout;
      }
    };
    useEffect(() => {
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current); 
      };
    }, []);
    
    return (
      <TabPanel {...rest}>
        <div className={style.tableContainer}>
          <table className={style.table}>
            <IndividualPanelHeader />
            <tbody>
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Candle Duration
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <EditableTableFieldNoOnBlurUpdate
                    color={colors.lightGrey}
                    backgroundColor="inherit"
                    fontSize={fontSizes.body3}
                    value={details.candleDuration}
                    setValue={(newValue) => {
                      const copy = cloneDeep(details);
                      copy.candleDuration = newValue;
                      setDetails(copy);
                    }}
                  />
                </td>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Time Unit
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <div className={style.inputWrapper}>
                    <TableSelect
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                      value={details.timeUnit}
                      options={[
                        { label: 'Minute', value: 'MINUTES' },
                        { label: 'Day', value: 'DAYS' },
                        { label: 'Week', value: 'WEEKS' },
                        { label: 'Month', value: 'MONTHS' },
                        { label: 'Quarter', value: 'QUARTERS' },
                        { label: 'Year', value: 'YEARS' },
                      ]}
                      onChange={(value) => {
                        const copy = cloneDeep(details);
                        copy.timeUnit = value;
                        setDetails(copy);
                      }}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Pivots as Trails
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <div className={style.inputWrapper}>
                    <CheckBox
                      fontSize={fontSizes.body3}
                      checked={details.usePivotsAsTrails}
                      onChange={(checked) => {
                        const copy = cloneDeep(details);
                        copy.usePivotsAsTrails = checked;
                        setDetails(copy);
                      }}
                    />
                  </div>
                </td>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    No Inside Candles
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <div className={style.inputWrapper}>
                    <CheckBox
                      fontSize={fontSizes.body3}
                      checked={details.filterInsideCandleMagnitudes}
                      onChange={(checked) => {
                        const copy = cloneDeep(details);
                        copy.filterInsideCandleMagnitudes = checked;
                        setDetails(copy);
                      }}
                    />
                  </div>
                </td>
              </tr>
              {details.usePivotsAsTrails ? (
                <>
                  <tr>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        Stop Placement
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <MultiSwitch
                          labels={['High/Low', 'Mid-Point']}
                          values={['high_low', 'mid']}
                          setValue={(value) => {
                            const copy = cloneDeep(details);
                            copy.exitInPivotCandle = value;
                            setDetails(copy);
                          }}
                          controlledValue={details.exitInPivotCandle}
                        />
                      </div>
                    </td>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        # of Pivots
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <Body3
                          fontWeight={700}
                          color={colors.lightGrey}
                          margin="3px 3px 0 0"
                        >
                          #
                        </Body3>
                        <EditableTableFieldNoOnBlurUpdate
                          color={colors.lightGrey}
                          backgroundColor="inherit"
                          fontSize={fontSizes.body3}
                          value={details.numberOfPivots}
                          setValue={(newValue) => {
                            const copy = cloneDeep(details);
                            copy.numberOfPivots = newValue;
                            setDetails(copy);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        Initial Stop
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <TableSelect
                          margin="5px auto 0 auto"
                          color={colors.lightGrey}
                          backgroundColor="inherit"
                          value={details.initialStop}
                          options={[
                            { label: 'None', value: 'NONE' },
                            {
                              label: 'Candle Retrace',
                              value: 'CANDLE_RETRACE',
                            },
                            { label: 'ATR', value: 'ATR' },
                            {
                              label: 'Candle High/Low',
                              value: 'CANDLE_HIGH_LOW',
                            },
                          ]}
                          onChange={(value) => {
                            const copy = cloneDeep(details);
                            copy.initialStop = value;
                            setDetails(copy);
                          }}
                        />
                      </div>
                    </td>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        1st Target Stop
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <TableSelect
                          margin="5px auto 0 auto"
                          color={colors.lightGrey}
                          backgroundColor="inherit"
                          value={details.firstTargetStop}
                          options={[
                            { label: 'None', value: 'NONE' },
                            {
                              label: 'Candle Retrace',
                              value: 'CANDLE_RETRACE',
                            },
                            { label: 'ATR', value: 'ATR' },
                            {
                              label: 'Candle High/Low',
                              value: 'CANDLE_HIGH_LOW',
                            },
                            { label: 'Break Even', value: 'BREAK_EVEN' },
                          ]}
                          onChange={(value) => {
                            const copy = cloneDeep(details);
                            copy.firstTargetStop = value;
                            setDetails(copy);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    {details.initialStop === 'NONE' ||
                    details.initialStop === 'CANDLE_HIGH_LOW' ? (
                        <>
                          <td></td>
                          <td></td>
                        </>
                      ) : (
                        <>
                          <td className={style.labelTd}>
                            <Body3 fontWeight={700} color={colors.lightGrey}>
                              {details.initialStop === 'ATR'
                                ? '% of ATR'
                                : 'Retrace %'}
                            </Body3>
                          </td>
                          <td className={style.inputTd}>
                            <div className={style.inputWrapper}>
                              <EditableTableFieldNoOnBlurUpdate
                                color={colors.lightGrey}
                                backgroundColor="inherit"
                                fontSize={fontSizes.body3}
                                value={
                                  details.initialStop === 'ATR'
                                    ? details.pctAtrInitialStop
                                    : details.pctRetraceInitialStop
                                }
                                setValue={(newValue) => {
                                  const copy = cloneDeep(details);
                                  if (details.initialStop === 'ATR')
                                    copy.pctAtrInitialStop = newValue;
                                  else copy.pctRetraceInitialStop = newValue;
                                  setDetails(copy);
                                }}
                              />
                              <Body3
                                fontWeight={700}
                                color={colors.lightGrey}
                                margin="3px 3px 0 0"
                              >
                              %
                              </Body3>
                            </div>
                          </td>
                        </>
                      )}

                    {details.firstTargetStop === 'NONE' ||
                    details.firstTargetStop === 'CANDLE_HIGH_LOW' ||
                    details.firstTargetStop === 'BREAK_EVEN' ? (
                        <>
                          <td></td>
                          <td></td>
                        </>
                      ) : (
                        <>
                          <td className={style.labelTd}>
                            <Body3 fontWeight={700} color={colors.lightGrey}>
                              {details.firstTargetStop === 'ATR'
                                ? '% of ATR'
                                : 'Retrace %'}
                            </Body3>
                          </td>
                          <td className={style.inputTd}>
                            <div className={style.inputWrapper}>
                              <EditableTableFieldNoOnBlurUpdate
                                color={colors.lightGrey}
                                backgroundColor="inherit"
                                fontSize={fontSizes.body3}
                                value={details.firstTargetStop === 'ATR' ? details.pctAtrFirstTargetStop : details.pctRetraceFirstTargetStop}
                                setValue={(newValue) => {
                                  const copy = cloneDeep(details);
                                  if (details.firstTargetStop === 'ATR') copy.pctAtrFirstTargetStop = newValue;
                                  else copy.pctRetraceFirstTargetStop = newValue;
                                  setDetails(copy);
                                }}
                              />
                              <Body3
                                fontWeight={700}
                                color={colors.lightGrey}
                                margin="3px 3px 0 0"
                              >
                              %
                              </Body3>
                            </div>
                          </td>
                        </>
                      )}
                  </tr>
                </>
              ) : null}
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Use ATR
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <div className={style.inputWrapper}>
                    <CheckBox
                      fontSize={fontSizes.body3}
                      checked={details.useAtr}
                      onChange={(checked) => {
                        const copy = cloneDeep(details);
                        copy.useAtr = checked;
                        setDetails(copy);
                      }}
                    />
                  </div>
                </td>
                <td></td>
                <td></td>
              </tr>
              {details.useAtr ? (
                <>
                  <tr>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        Candles Back
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <Body3
                          fontWeight={700}
                          color={colors.lightGrey}
                          margin="3px 3px 0 0"
                        >
                          #
                        </Body3>
                        <EditableTableFieldNoOnBlurUpdate
                          color={colors.lightGrey}
                          backgroundColor="inherit"
                          fontSize={fontSizes.body3}
                          value={details.ATR.candlesBack}
                          setValue={(newValue) => {
                            const copy = cloneDeep(details);
                            copy.ATR.candlesBack = newValue;
                            setDetails(copy);
                          }}
                        />
                      </div>
                    </td>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        ATR Type
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <TableSelect
                          color={colors.lightGrey}
                          backgroundColor="inherit"
                          value={details.ATR.atrType}
                          options={[
                            { label: 'Wilder', value: 'WILDER' },
                            {
                              label: 'SMA',
                              value: 'SMA',
                            },
                            { label: 'SMA No Gap', value: 'SMA_NO_GAP' },
                          ]}
                          onChange={(value) => {
                            const copy = cloneDeep(details);
                            copy.ATR.atrType = value;
                            setDetails(copy);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        ATR Candle Duration
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <EditableTableFieldNoOnBlurUpdate
                        color={colors.lightGrey}
                        backgroundColor="inherit"
                        fontSize={fontSizes.body3}
                        value={details.ATR.candleDuration}
                        setValue={(newValue) => {
                          const copy = cloneDeep(details);
                          copy.ATR.candleDuration = newValue;
                          setDetails(copy);
                        }}
                      />
                    </td>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        Time Unit
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <TableSelect
                          color={colors.lightGrey}
                          backgroundColor="inherit"
                          value={details.ATR.timeUnit}
                          options={[
                            { label: 'Minute', value: 'MINUTES' },
                            { label: 'Day', value: 'DAYS' },
                            { label: 'Week', value: 'WEEKS' },
                            { label: 'Month', value: 'MONTHS' },
                            { label: 'Quarter', value: 'QUARTERS' },
                            { label: 'Year', value: 'YEARS' },
                          ]}
                          onChange={(value) => {
                            const copy = cloneDeep(details);
                            copy.ATR.timeUnit = value;
                            setDetails(copy);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        Filter 1st Mag.
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <CheckBox
                          // margin="5px 0 0 0"
                          fontSize={fontSizes.body3}
                          checked={details.filterFirstMagnitudeBasedOnAtr}
                          onChange={(checked) => {
                            const copy = cloneDeep(details);
                            copy.filterFirstMagnitudeBasedOnAtr = checked;
                            setDetails(copy);
                          }}
                        />
                      </div>
                    </td>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        Filter 1st Mag %
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <EditableTableFieldNoOnBlurUpdate
                          color={colors.lightGrey}
                          backgroundColor="inherit"
                          fontSize={fontSizes.body3}
                          value={details.pctOfAtrForFilter}
                          setValue={(newValue) => {
                            const copy = cloneDeep(details);
                            copy.pctOfAtrForFilter = newValue;
                            setDetails(copy);
                          }}
                        />
                        <Body3
                          fontWeight={700}
                          color={colors.lightGrey}
                          margin="3px 3px 0 0"
                        >
                          %
                        </Body3>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        Filter All Mag.
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <CheckBox
                          // margin="5px 0 0 0"
                          fontSize={fontSizes.body3}
                          checked={details.filterAllMagnitudeBasedOnAtr}
                          onChange={(checked) => {
                            const copy = cloneDeep(details);
                            copy.filterAllMagnitudeBasedOnAtr = checked;
                            setDetails(copy);
                          }}
                        />
                      </div>
                    </td>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        Filter All Mag %
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <EditableTableFieldNoOnBlurUpdate
                          color={colors.lightGrey}
                          backgroundColor="inherit"
                          fontSize={fontSizes.body3}
                          value={details.pctOfAtrForFilterAllMag}
                          setValue={(newValue) => {
                            const copy = cloneDeep(details);
                            copy.pctOfAtrForFilterAllMag = newValue;
                            setDetails(copy);
                          }}
                        />
                        <Body3
                          fontWeight={700}
                          color={colors.lightGrey}
                          margin="3px 3px 0 0"
                        >
                          %
                        </Body3>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        % of ATR as Mag.
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <CheckBox
                          // margin="5px 0 0 0"
                          fontSize={fontSizes.body3}
                          checked={details.useAtrInsteadOfMag}
                          onChange={(checked) => {
                            const copy = cloneDeep(details);
                            copy.useAtrInsteadOfMag = checked;
                            setDetails(copy);
                          }}
                        />
                      </div>
                    </td>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        ATR as Mag %
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <EditableTableFieldNoOnBlurUpdate
                          color={colors.lightGrey}
                          backgroundColor="inherit"
                          fontSize={fontSizes.body3}
                          value={details.pctAtrInsteadOfMag}
                          setValue={(newValue) => {
                            const copy = cloneDeep(details);
                            copy.pctAtrInsteadOfMag = newValue;
                            setDetails(copy);
                          }}
                        />
                        <Body3
                          fontWeight={700}
                          color={colors.lightGrey}
                          margin="3px 3px 0 0"
                        >
                          %
                        </Body3>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                      Extend Pivots w/ATR
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <CheckBox
                          // margin="5px 0 0 0"
                          fontSize={fontSizes.body3}
                          checked={details.generateFromAtrWhenNoPivots}
                          onChange={(checked) => {
                            const copy = cloneDeep(details);
                            copy.generateFromAtrWhenNoPivots = checked;
                            setDetails(copy);
                          }}
                        />
                      </div>
                    </td>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                      Extend Pivots w/ATR %
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <EditableTableFieldNoOnBlurUpdate
                          color={colors.lightGrey}
                          backgroundColor="inherit"
                          fontSize={fontSizes.body3}
                          value={details.pctAtrGenerateAtrWhenNoPivots}
                          setValue={(newValue) => {
                            const copy = cloneDeep(details);
                            copy.pctAtrGenerateAtrWhenNoPivots = newValue;
                            setDetails(copy);
                          }}
                        />
                        <Body3
                          fontWeight={700}
                          color={colors.lightGrey}
                          margin="3px 3px 0 0"
                        >
                          %
                        </Body3>
                      </div>
                    </td>
                  </tr>
                </>
              ) : null}
              <tr>
                <td className={style.fieldHeader}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Pct Per Pivot List
                  </Body3>
                </td>
                <td className={style.fieldHeader}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    #
                  </Body3>
                </td>
                <td className={style.valueHeader}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Pct. Per Pivot
                  </Body3>
                </td>
                <td className={style.fieldHeader}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Add/Remove
                  </Body3>
                </td>
              </tr>
              {details.qtyPctPerPivot.map((pct, index) => {
                return (
                  <tr key={'' + pct + index}>
                    <td></td>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        {index + 1}
                      </Body3>
                    </td>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        {Number(pct) * 100}%
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <CancelOrderButton
                          margin="5px 0 0 0"
                          width={15}
                          clickHandler={() => {
                            const filteredArr = details.qtyPctPerPivot.filter(
                              (item, i) => i !== index
                            );
                            const copy = cloneDeep(details);
                            copy.qtyPctPerPivot = filteredArr;
                            setDetails(copy);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td></td>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Add
                  </Body3>
                </td>
                <td className={style.labelTd}>
                  <div className={style.inputWrapper}>
                    <EditableTableFieldNoOnBlurUpdate
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                      fontSize={fontSizes.body3}
                      value={addValue}
                      setValue={setAddValue}
                    />
                    <Body3
                      fontWeight={700}
                      color={colors.lightGrey}
                      margin="3px 3px 0 0"
                    >
                      %
                    </Body3>
                  </div>
                </td>
                <td className={style.inputTd}>
                  <div className={style.inputWrapper}>
                    <IncrementButton
                      margin="5px 0 0 0"
                      color={colors.white}
                      backgroundColor={colors.blue}
                      width={10}
                      stroke={1}
                      handleClick={() => {
                        const newArr = [...details.qtyPctPerPivot];
                        newArr.push(Number(addValue) / 100);
                        const copy = cloneDeep(details);
                        copy.qtyPctPerPivot = newArr;
                        setDetails(copy);
                      }}
                      buttonWidth={15}
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <header className={style.header}>
            <Body3 color={headerTextColor} fontWeight={700}>
              {headerText}
            </Body3>
            <TextButton
              disabled={tradePlan.exitTactics['CANDLE_MAGNITUDE']}
              color={colors.lightGrey}
              backgroundColor={
                tradePlan.exitTactics['CANDLE_MAGNITUDE']
                  ? colors.grey
                  : colors.blue
              }
              fontSize="small"
              clickHandler={addTactic}
              width="20%"
              height="50%"
            >
              Add
            </TextButton>
          </header>
        </div>
      </TabPanel>
    );
  }
);

AddCandleMagnitudeTacticPanel.tabsRole = 'TabPanel';
